// Phase 15 Plan 09 Task 1 — TournamentsScreen.
//
// Top-level Tournaments tab. Surface order (UI-SPEC §"Tournament screen
// primary focal point"):
//   1. Active tournaments (top) — sourced from $lobbyData.active_tournaments
//      Each TournamentPreview rendered as <TournamentCard />.
//   2. Join-by-code section title + primary Sun-Yellow CTA → opens
//      TournamentJoinModal.
//   3. TournamentHistoryAccordion (bottom, collapsed by default).
//
// Section-stack pattern modeled after SettingsScreen.tsx.
//
// Tournament-joined event:
//   The TournamentJoinModal dispatches `runegambit:tournament-joined` on
//   success. The `$tournamentList` store has its own listener wired via
//   initTournamentListListeners() (Plan 05) which refreshes the lobby —
//   no per-screen wiring needed here; the screen re-renders when
//   $lobbyData changes.

import { useStore } from '@nanostores/react'
import { type JSX, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { useDocumentTitle } from '../../i18n/useDocumentTitle.js'
import { useRouter } from '../../router/router.js'
import { $lobbyData } from '../../stores/lobbyData.js'
import { Btn } from '../components/Btn'
import { Screen, ScreenHeader } from '../components/Screen.js'
import { TournamentCard } from './TournamentCard.js'
import { TournamentHistoryAccordion } from './TournamentHistoryAccordion.js'
import { TournamentJoinModal } from './TournamentJoinModal.js'

const sectionStyle: React.CSSProperties = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-3)',
}

const sectionHeading: React.CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-family-display)',
  fontWeight: 700,
  fontSize: 'var(--font-size-16)',
  color: 'var(--color-text-primary)',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  opacity: 0.9,
}

export function TournamentsScreen(): JSX.Element {
  useDocumentTitle('doc.title.tournaments')
  const intl = useIntl()
  const router = useRouter()
  const lobby = useStore($lobbyData)
  const tournaments = lobby?.active_tournaments ?? []
  const [joinModalOpen, setJoinModalOpen] = useState(false)

  return (
    <Screen
      name="tournaments"
      gutter={16}
      contentStyle={{ gap: 'var(--space-5)', alignItems: 'center' }}
      header={
        <ScreenHeader
          title={
            <FormattedMessage
              id="social.tournaments.title"
              defaultMessage="Tournaments"
            />
          }
          onBack={() => router.navigate('/lobby')}
          backLabel={intl.formatMessage({
            id: 'nav.back',
            defaultMessage: 'Back',
          })}
        />
      }
    >
      <section
        aria-labelledby="tournaments-active-heading"
        data-section="active"
        style={sectionStyle}
      >
        <h2 id="tournaments-active-heading" style={sectionHeading}>
          <FormattedMessage
            id="social.tournaments.active.title"
            defaultMessage="Your tournaments"
          />
        </h2>
        {tournaments.length === 0 ? (
          <div
            data-testid="tournaments-empty"
            className="card"
            style={{
              // Let the folk `.card` recipe own the surface (borderless
              // near-white + warm shadow); only layout overrides here.
              padding: 'var(--space-5)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                fontSize: 'var(--font-size-16)',
                color: 'var(--color-text-primary)',
              }}
            >
              <FormattedMessage
                id="social.tournaments.empty.title"
                defaultMessage="No tournaments yet"
              />
            </p>
            <p
              style={{
                margin: 0,
                fontFamily: 'var(--font-family-body)',
                fontWeight: 500,
                fontSize: 'var(--font-size-14)',
                color: 'var(--color-text-tertiary)',
              }}
            >
              <FormattedMessage
                id="social.tournaments.empty.body"
                defaultMessage="Join by code to start playing."
              />
            </p>
          </div>
        ) : (
          tournaments.map((t) => (
            <TournamentCard key={t.tournament_id} preview={t} />
          ))
        )}
      </section>

      <section
        aria-labelledby="tournaments-join-heading"
        data-section="join"
        style={sectionStyle}
      >
        <h2 id="tournaments-join-heading" style={sectionHeading}>
          <FormattedMessage
            id="social.tournaments.join.title"
            defaultMessage="Join a tournament"
          />
        </h2>
        <Btn
          kind="primary"
          size="lg"
          testId="tournament-join-open"
          onClick={() => setJoinModalOpen(true)}
        >
          <FormattedMessage
            id="social.tournaments.join_cta"
            defaultMessage="Join by code"
          />
        </Btn>
      </section>

      <section
        aria-label={intl.formatMessage({
          id: 'social.tournaments.past_section_aria',
        })}
        data-section="history"
        style={sectionStyle}
      >
        <TournamentHistoryAccordion />
      </section>

      <TournamentJoinModal
        open={joinModalOpen}
        onClose={() => setJoinModalOpen(false)}
      />
    </Screen>
  )
}
